// src/pages/TeamPage.tsx
import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Users,
  Search,
  Filter,
  Mail,
  Link as LinkIcon,
  Copy,
} from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import TeamMemberCard from './TeamMemberCard';
import InviteTeamMemberModal from './InviteTeamMemberModal';
import { useSchool } from '../../contexts/SchoolContext';
import toast from 'react-hot-toast';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

interface TeamMember {
  id: string;
  displayName: string;
  email: string;
  role: 'admin' | 'staff';
  department?: string;
  title?: string;
  status: 'online' | 'away' | 'offline';
  lastActive?: string;
  joinedAt: string;
}

export default function TeamPage() {
  useDocumentTitle('Team');
  
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'staff'>(
    'all'
  );
  const [showInviteModal, setShowInviteModal] = useState(false);
  const { selectedSchool } = useSchool();

  useEffect(() => {
    const loadTeamMembers = async () => {
      if (!selectedSchool) {
        console.log('[TeamPage] No school selected, skipping member load');
        setLoading(false);
        return;
      }

      console.log('[TeamPage] Loading members for school:', selectedSchool.id);

      try {
        // Get all school_member records for this school
        const membersQuery = query(
          collection(db, 'school_members'),
          where('schoolId', '==', selectedSchool.id)
        );
        const memberSnapshot = await getDocs(membersQuery);
        console.log('[TeamPage] Found members:', memberSnapshot.size);

        // For each membership, fetch the corresponding user document
        const memberPromises = memberSnapshot.docs.map(async (memberDoc) => {
          const { userId, role, joinedAt } = memberDoc.data();

          // Directly fetch the user by ID
          const userRef = doc(db, 'users', userId);
          const userSnap = await getDoc(userRef);

          if (!userSnap.exists()) {
            console.log('[TeamPage] No user doc for:', userId);
            return null;
          }

          const userData = userSnap.data();
          console.log('[TeamPage] Found user data:', userData);

          // Prefer fullName, then displayName, then email prefix
          const displayName =
            userData.fullName ||
            userData.displayName ||
            (userData.email ? userData.email.split('@')[0] : 'Unknown User');

          return {
            id: userId,
            displayName,
            email: userData.email || '',
            role: role || 'staff',
            department: userData.department,
            title: userData.title,
            status: userData.status || 'offline',
            lastActive: userData.lastActive,
            joinedAt,
          } as TeamMember;
        });

        console.log('[TeamPage] Waiting for all member data...');
        const fetched = await Promise.all(memberPromises);
        const validMembers = fetched.filter(
          (m): m is TeamMember => m !== null
        );
        console.log('[TeamPage] Final member list:', validMembers);

        setMembers(validMembers);
      } catch (error) {
        console.error('[TeamPage] Error loading team members:', error);
        toast.error('Failed to load team members');
      } finally {
        setLoading(false);
      }
    };

    loadTeamMembers();
  }, [selectedSchool]);

  const departments = Array.from(
    new Set(members.map((m) => m.department).filter(Boolean))
  );

  const filteredMembers = members.filter((member) => {
    const matchesSearch =
      member.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment =
      departmentFilter === 'all' || member.department === departmentFilter;
    const matchesRole =
      roleFilter === 'all' || member.role === roleFilter;
    return matchesSearch && matchesDepartment && matchesRole;
  });

  const getSignupLink = () => {
    if (!selectedSchool) return '';
    const origin = window.location.origin;
    return `${origin}/signup?code=${selectedSchool.code}`;
  };

  const handleCopyLink = () => {
    const link = getSignupLink();
    navigator.clipboard.writeText(link);
    toast.success('Signup link copied to clipboard');
  };

  const handleCopyCode = () => {
    if (selectedSchool?.code) {
      navigator.clipboard.writeText(selectedSchool.code);
      toast.success('School code copied to clipboard');
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Loading team members...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Users className="w-8 h-8 text-primary-500" />
              <div>
                <h1 className="text-2xl font-semibold text-text-primary">
                  Team
                </h1>
                <p className="text-text-secondary">
                  {members.length} team members
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCopyLink}
                className="px-4 py-2 bg-surface-dark text-text-primary rounded-lg hover:bg-background 
                         transition-colors flex items-center gap-2"
              >
                <LinkIcon className="w-5 h-5" />
                Copy Signup Link
              </button>
              <button
                onClick={() => setShowInviteModal(true)}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                         transition-colors flex items-center gap-2"
              >
                <Mail className="w-5 h-5" />
                Invite Member
              </button>
            </div>
          </div>

          {/* School Code Box */}
          <div className="bg-surface-light rounded-lg p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-medium text-text-primary">
                  School Code
                </h3>
                <p className="text-sm text-text-secondary mt-1">
                  Share this code with team members to join directly
                </p>
              </div>
              <div className="flex items-center gap-2 bg-background rounded-lg p-2 border border-border-light">
                <span className="text-xl font-mono font-semibold text-primary-500 px-4">
                  {selectedSchool?.code || 'No code available'}
                </span>
                <button
                  onClick={handleCopyCode}
                  className="p-2 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5 text-text-secondary hover:text-primary-500" />
                </button>
              </div>
            </div>
          </div>

          {/* Shareable Link */}
          <div className="bg-surface-light rounded-lg p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-medium text-text-primary">
                  Shareable Signup Link
                </h3>
                <p className="text-sm text-text-secondary mt-1">
                  Share this link with new team members to join directly
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
                <div className="relative flex-1 md:w-96">
                  <input
                    type="text"
                    value={getSignupLink()}
                    readOnly
                    className="w-full px-4 py-2 bg-background rounded-lg border border-border-light 
                             text-text-secondary pr-24 overflow-hidden text-ellipsis"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="absolute right-1 top-1 bottom-1 px-4 bg-primary-500 text-white 
                             rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-surface-light rounded-lg p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search team members..."
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-border-light 
                             focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 
                             outline-none transition-all"
                  />
                </div>
              </div>

              {/* Department Filter */}
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-text-secondary" />
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                >
                  <option value="all">All Departments</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>

              {/* Role Filter */}
              <select
                value={roleFilter}
                onChange={(e) =>
                  setRoleFilter(e.target.value as 'all' | 'admin' | 'staff')
                }
                className="px-3 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              >
                <option value="all">All Roles</option>
                <option value="admin">Administrators</option>
                <option value="staff">Staff Members</option>
              </select>
            </div>
          </div>

          {/* Team Members Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMembers.map((member) => (
              <TeamMemberCard key={member.id} member={member} />
            ))}

            {filteredMembers.length === 0 && (
              <div className="col-span-full text-center py-8">
                <Users className="w-12 h-12 text-text-secondary mx-auto mb-3" />
                <p className="text-text-secondary">No team members found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showInviteModal && (
        <InviteTeamMemberModal onClose={() => setShowInviteModal(false)} />
      )}
    </AppLayout>
  );
}
